import { useState } from 'react'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertCircle, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import type { ShippingAddress } from '@/types/database'

const US_STATES: Record<string, string> = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', DC: 'District of Columbia',
  FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois',
  IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana',
  ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota',
  MS: 'Mississippi', MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada',
  NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York',
  NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon',
  PA: 'Pennsylvania', PR: 'Puerto Rico', RI: 'Rhode Island', SC: 'South Carolina',
  SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont',
  VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
}

const CA_PROVINCES: Record<string, string> = {
  AB: 'Alberta', BC: 'British Columbia', MB: 'Manitoba', NB: 'New Brunswick',
  NL: 'Newfoundland and Labrador', NS: 'Nova Scotia', NT: 'Northwest Territories',
  NU: 'Nunavut', ON: 'Ontario', PE: 'Prince Edward Island', QC: 'Quebec',
  SK: 'Saskatchewan', YT: 'Yukon',
}

// Normalize full country names to ISO codes
function normalizeCountry(value: string | undefined): string {
  if (!value) return 'US'
  const trimmed = value.trim()
  if (trimmed.length === 2) return trimmed.toUpperCase()
  const lower = trimmed.toLowerCase()
  if (lower === 'united states' || lower === 'united states of america' || lower === 'usa') return 'US'
  if (lower === 'canada') return 'CA'
  return 'US'
}

// Normalize full state names to abbreviations
function normalizeState(value: string | undefined, country: string): string {
  if (!value) return ''
  const trimmed = value.trim()
  if (trimmed.length <= 2) return trimmed.toUpperCase()
  const lower = trimmed.toLowerCase()
  const lookup = country === 'CA' ? CA_PROVINCES : US_STATES
  for (const [abbr, name] of Object.entries(lookup)) {
    if (name.toLowerCase() === lower) return abbr
  }
  return trimmed
}

interface AddressFormProps {
  profileId: string
  address: ShippingAddress | null
}

export function AddressForm({ profileId, address }: AddressFormProps) {
  const initialCountry = normalizeCountry(address?.country)
  const initialState = normalizeState(address?.state, initialCountry)

  const [formData, setFormData] = useState<ShippingAddress>({
    name: address?.name || '',
    line1: address?.line1 || '',
    line2: address?.line2 || '',
    city: address?.city || '',
    state: initialState,
    postal_code: address?.postal_code || '',
    country: initialCountry,
  })
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const handleChange = (field: keyof ShippingAddress, value: string) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value }
      // Reset state when country changes
      if (field === 'country' && value !== prev.country) {
        updated.state = ''
      }
      return updated
    })
  }

  const stateOptions = formData.country === 'CA' ? CA_PROVINCES : US_STATES

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsLoading(true)

    try {
      // Clean up empty fields
      const cleanedAddress: ShippingAddress = {
        ...formData,
        line2: formData.line2 || undefined,
      }

      const { error: updateError } = await supabase
        .from('customer_profiles')
        .update({
          default_shipping_address: cleanedAddress,
        })
        .eq('id', profileId)

      if (updateError) {
        setError(updateError.message)
        return
      }

      toast.success('Address updated successfully')
    } catch {
      setError('An unexpected error occurred')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold">Shipping Address</h3>
        <p className="text-sm text-muted-foreground">
          Used to prefill checkout. Updated automatically after successful orders.
        </p>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-2">
        <Label htmlFor="name">Full Name</Label>
        <Input
          id="name"
          type="text"
          placeholder="John Doe"
          value={formData.name}
          onChange={(e) => handleChange('name', e.target.value)}
          disabled={isLoading}
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="line1">Address Line 1</Label>
        <Input
          id="line1"
          type="text"
          placeholder="123 Main Street"
          value={formData.line1}
          onChange={(e) => handleChange('line1', e.target.value)}
          disabled={isLoading}
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="line2">Address Line 2 (Optional)</Label>
        <Input
          id="line2"
          type="text"
          placeholder="Apt, Suite, etc."
          value={formData.line2 || ''}
          onChange={(e) => handleChange('line2', e.target.value)}
          disabled={isLoading}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="city">City</Label>
          <Input
            id="city"
            type="text"
            placeholder="New York"
            value={formData.city}
            onChange={(e) => handleChange('city', e.target.value)}
            disabled={isLoading}
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="state">{formData.country === 'CA' ? 'Province' : 'State'}</Label>
          <Select
            value={formData.state}
            onValueChange={(value) => handleChange('state', value)}
            disabled={isLoading}
          >
            <SelectTrigger id="state">
              <SelectValue placeholder="Select..." />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(stateOptions).map(([abbr, name]) => (
                <SelectItem key={abbr} value={abbr}>{name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="postal_code">{formData.country === 'CA' ? 'Postal Code' : 'ZIP Code'}</Label>
          <Input
            id="postal_code"
            type="text"
            placeholder={formData.country === 'CA' ? 'A1A 1A1' : '10001'}
            value={formData.postal_code}
            onChange={(e) => handleChange('postal_code', e.target.value)}
            disabled={isLoading}
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="country">Country</Label>
          <Select
            value={formData.country}
            onValueChange={(value) => handleChange('country', value)}
            disabled={isLoading}
          >
            <SelectTrigger id="country">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="US">United States</SelectItem>
              <SelectItem value="CA">Canada</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <Button type="submit" disabled={isLoading}>
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Saving...
          </>
        ) : (
          'Save Address'
        )}
      </Button>
    </form>
  )
}
